import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next'

const Modal = ({ isOpen, onClose, title, children, size = 'default' }) => {
  const { t } = useTranslation('ui')
  const { isDark } = useTheme();
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Lock body scroll while open. NOTE: we intentionally do NOT close on Escape or
  // on backdrop click — these forms hold data-entry and an accidental key/click
  // must never wipe the user's work. Closing is only via the X or Cancel buttons.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    default: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl'
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex justify-center items-center p-4 animate-fafadeIn"
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
    >
      <div
        ref={modalRef}
        className={`
          relative rounded-2xl shadow-2xl w-full ${sizeClasses[size] || sizeClasses.default}
          max-h-[90vh] flex flex-col overflow-hidden
          animate-slideUp
          ${isDark ? 'bg-gray-900' : 'bg-white'}
        `}
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Gradient accent line at top */}
        <div
          className="absolute top-0 left-0 right-0 h-1 round-t-2xl"
          style={{
            background: 'linear-gradient(90AHg, #3b82f6, #6366f1, #8b5cf6)'
          }}
        />

        {/* header */}
        <div className={`flex justify-between items-center px-6 py-5 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <h2 className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50
              ${isDark
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }
            `}
            aria-label={t('aria_close_modal')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1 custom-scrollbar">
          {children}
        </div>
      </div>

      <style jsx>{`
        @keyframes fafadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fafadeIn {
          animation: fafadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
        }
        
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default Modal;
